import { cn } from '@/lib/cn';
import {
  createResponseProse,
  responseBodyTextClass,
  responseQuestionWidthClass,
  useProseDensity,
  type ProseDensity,
  type ResponseProseComponents,
} from '@heidi/answer-kit';
import { FileCard } from '@heidi/design-system/file-card';
import { TypographyH3, TypographyP } from '@heidi/design-system/typography';
import { isImageType } from '@heidi/utils';
import {
  splitMarkdownTables,
  type MarkdownTableSegment,
} from '@heidi/utils/split-markdown-tables';
import { Fragment, memo, useMemo, type JSX } from 'react';

// ===== USER MESSAGE COMPONENT =====

/** Maximum character length for displaying user queries in branded H3 style.
 * Queries longer than this will use the chat bubble style instead. */
const USER_QUERY_BRANDED_STYLE_MAX_LENGTH = 100;

const TABLE_PROSE_BY_DENSITY: Record<ProseDensity, ResponseProseComponents> = {
  comfortable: createResponseProse({ density: 'comfortable', tintedCell: true }),
  compact: createResponseProse({ density: 'compact', tintedCell: true }),
};

const renderCellLines = (lines: string[]) =>
  lines.map((line, index) => (
    <Fragment key={index}>
      {index > 0 && <br />}
      {line}
    </Fragment>
  ));

function UserMessageTable({
  header,
  rows,
  density,
}: Extract<MarkdownTableSegment, { kind: 'table' }> & { density: ProseDensity }) {
  const { table: Table, thead: Thead, th: Th, td: Td } = TABLE_PROSE_BY_DENSITY[density];
  return (
    <Table>
      <Thead>
        <tr>
          {header.map((cell, column) => (
            <Th key={column}>{renderCellLines(cell)}</Th>
          ))}
        </tr>
      </Thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, column) => (
              <Td key={column}>{renderCellLines(cell)}</Td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

interface UserMessageProps {
  message: string;
  files?: Array<{
    name: string;
    type: string;
    file_key?: string;
    url?: string; // Presigned URL for displaying images/files
  }>;
  className?: string;
  /** Unique identifier for scroll navigation */
  messageId?: string;
  /** Whether this is the first user message in the conversation */
  isFirstUserMessage?: boolean;
  /** Whether the chat is displayed in full-screen mode (vs sidebar) */
  isFullScreenEvidence?: boolean;
}

// User query styled conditionally: first short query in full-screen uses branded H3 style,
// all others use chat bubble (EVI-265)
// OPTIMIZATION: Wrapped in memo to prevent re-renders when parent re-renders
// but props haven't changed (e.g., during streaming state updates)
export const UserMessage = memo(function UserMessage({
  message,
  files,
  className,
  messageId,
  isFirstUserMessage = false,
  isFullScreenEvidence = false,
}: UserMessageProps): JSX.Element {
  // `isFullScreenEvidence` is the fallback for a bubble rendered outside a
  // density-publishing host; `Chat` publishes one from the same signal.
  const bubbleDensity = useProseDensity(isFullScreenEvidence ? 'comfortable' : 'compact');
  const hasFiles = files && files.length > 0;
  const segments = useMemo(() => splitMarkdownTables(message), [message]);
  // Only use branded style when ALL conditions are met:
  // 1. Is the first user message
  // 2. Is in full-screen evidence mode (not sidebar)
  // 3. Has no file attachments
  // 4. Message is short enough
  // 5. Message has no table
  const useBrandedStyle =
    isFirstUserMessage &&
    isFullScreenEvidence &&
    !hasFiles &&
    message.length <= USER_QUERY_BRANDED_STYLE_MAX_LENGTH &&
    segments.every((segment) => segment.kind === 'text');

  // Short queries without files: use branded Exposure H3 style
  if (useBrandedStyle) {
    return (
      <div
        className={cn('flex w-full min-w-0 scroll-mt-2.5', className)}
        data-message-id={messageId}
        data-user-message="true"
      >
        {/*
          - text-text-secondary is intentional: H3 is WCAG large text (24px),
            3:1 threshold.
          - font-light overrides TypographyH3's default font-medium (500) to
            Exposure Light (300) per the branded-title display treatment.
            Exposure is already inherited from the theme-level rule on h1-h3.
          - No vertical margin of its own, top or bottom. Both gaps around a
            question belong to the thread, not to this shape: `mb-6` here is
            why a short question sat 40px above its answer and a long one
            16px, and `mt-2.5` was an extra 10px on top of whatever the column
            had already spent. `data-user-message` is the hook the turn
            container's question-to-answer rule selects on, so every question
            shape in both chat families takes the same rung — see
            `response-thread`. `scroll-mt-*` stays: it is snap offset, not
            spacing.
        */}
        <TypographyH3
          className="break-words font-light text-text-secondary"
          data-chat-branded-title="true"
        >
          {message}
        </TypographyH3>
      </div>
    );
  }

  // Long queries or queries with files: use chat bubble style.
  // The question sits directly above the answer, so its size is the answer's —
  // read from the same density the prose uses rather than branched here. This
  // previously read `isFullScreenEvidence ? 'text-base' : 'sm:text-sm'`, which
  // had no base size at all: below the `sm` VIEWPORT breakpoint it fell through
  // to the component default, and above it the bubble sat at 14px next to a
  // 16px answer.
  return (
    <div
      className={cn('flex w-full scroll-mt-2.5 items-start justify-end', className)}
      data-message-id={messageId}
      data-user-message="true"
    >
      <div
        className={cn(
          'box-border flex flex-col items-start justify-end gap-1 rounded-lg bg-background-secondary px-3 py-1.5',
          responseQuestionWidthClass(bubbleDensity)
        )}
      >
        {hasFiles && (
          <div className="mb-1 flex flex-wrap gap-1.5">
            {files.map((file, index) =>
              file.url && isImageType(file.type) ? (
                <div
                  key={file.file_key || `${file.name}-${index}`}
                  className="size-14 overflow-hidden rounded-lg border border-border bg-surface"
                  data-file-key={file.file_key}
                >
                  <img
                    src={file.url}
                    alt={file.name}
                    className="size-full object-cover"
                  />
                </div>
              ) : (
                <FileCard
                  key={file.file_key || `${file.name}-${index}`}
                  fileName={file.name}
                  fileType={file.name.split('.').pop()?.toUpperCase()}
                />
              )
            )}
          </div>
        )}
        {segments.map((segment, index) =>
          segment.kind === 'table' ? (
            <UserMessageTable key={index} {...segment} density={bubbleDensity} />
          ) : (
            <TypographyP
              key={index}
              className={cn(
                // `[overflow-wrap:anywhere]` counts its break points in min-content, so a long
                // unbroken string (a preview URL's delivery token) wraps inside the `max-w-*` cap.
                // `break-words` is the fallback where `anywhere` is unsupported (Safari < 15.4).
                'w-full whitespace-pre-wrap break-words text-text-primary [overflow-wrap:anywhere]',
                responseBodyTextClass(bubbleDensity)
              )}
            >
              {segment.text}
            </TypographyP>
          )
        )}
      </div>
    </div>
  );
});
